import { useEffect, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';

/** Perspective of the card (px) and the largest tilt (deg) on each axis. */
const PERSPECTIVE = 900;
const MAX_TILT = 6;
const LIFT = 5;

const FLAT = `perspective(${PERSPECTIVE}px) translate3d(0,0,0) rotateX(0deg) rotateY(0deg)`;

/**
 * Pointer-driven tilt, without any React state. The pointer position is stored on every
 * move and applied once per animation frame: a `transform` and four CSS custom properties
 * (light position, shadow offset), nothing else. `will-change` is only set while the
 * card is hovered. Fine pointers only (mouse, trackpad, pen); never with reduced motion
 * and never on touch, where the card simply stays flat.
 */
function useTilt(outer: RefObject<HTMLDivElement>, card: RefObject<HTMLDivElement>) {
	useEffect(() => {
		const host = outer.current;
		const el = card.current;
		if (!host || !el) return;
		const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
		const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		if (!finePointer || reduced) return;

		let raf = 0;
		let x = 0;
		let y = 0;

		const apply = () => {
			raf = 0;
			const rect = host.getBoundingClientRect();
			if (!rect.width || !rect.height) return;
			const px = Math.min(1, Math.max(0, (x - rect.left) / rect.width));
			const py = Math.min(1, Math.max(0, (y - rect.top) / rect.height));
			const dx = (px - 0.5) * 2;
			const dy = (py - 0.5) * 2;
			el.style.transform = `perspective(${PERSPECTIVE}px) translate3d(0,${-LIFT}px,0) rotateX(${(-dy * MAX_TILT).toFixed(2)}deg) rotateY(${(dx * MAX_TILT).toFixed(2)}deg)`;
			el.style.setProperty('--gx', `${(px * 100).toFixed(1)}%`);
			el.style.setProperty('--gy', `${(py * 100).toFixed(1)}%`);
			el.style.setProperty('--sx', (-dx * 14).toFixed(1));
			el.style.setProperty('--sy', (-dy * 8).toFixed(1));
		};
		const schedule = () => {
			if (!raf) raf = requestAnimationFrame(apply);
		};

		const onEnter = (e: PointerEvent) => {
			if (e.pointerType === 'touch') return;
			x = e.clientX;
			y = e.clientY;
			el.style.willChange = 'transform';
			el.dataset.active = 'true';
			schedule();
		};
		const onMove = (e: PointerEvent) => {
			if (e.pointerType === 'touch' || !el.dataset.active) return;
			x = e.clientX;
			y = e.clientY;
			schedule();
		};
		const onLeave = () => {
			if (raf) cancelAnimationFrame(raf);
			raf = 0;
			delete el.dataset.active;
			el.style.transform = FLAT;
			el.style.setProperty('--sx', '0');
			el.style.setProperty('--sy', '0');
		};
		// The card is back to rest: release the compositor layer.
		const onEnd = (e: TransitionEvent) => {
			if (e.target === el && e.propertyName === 'transform' && !el.dataset.active) el.style.willChange = '';
		};

		host.addEventListener('pointerenter', onEnter);
		host.addEventListener('pointermove', onMove);
		host.addEventListener('pointerleave', onLeave);
		el.addEventListener('transitionend', onEnd);
		return () => {
			if (raf) cancelAnimationFrame(raf);
			host.removeEventListener('pointerenter', onEnter);
			host.removeEventListener('pointermove', onMove);
			host.removeEventListener('pointerleave', onLeave);
			el.removeEventListener('transitionend', onEnd);
			el.style.willChange = '';
		};
	}, [outer, card]);
}

type TiltCardProps = {
	children: ReactNode;
	/** Placement of the card in its layout. */
	className?: string;
	/** Corner radius of the card (the light and the clipping follow it). */
	radius?: string;
};

/**
 * A floating card: tilts toward the pointer (perspective 900px, 6deg at most), lifts a
 * few pixels, its shadow shifts the other way, and a soft white reflection follows the
 * cursor. The listeners sit on a fixed outer box so the tilt never moves the area that
 * receives the pointer (no flicker at the edges). The content is never scaled.
 */
export function TiltCard({ children, className = '', radius = 'rounded-card' }: TiltCardProps) {
	const outer = useRef<HTMLDivElement>(null);
	const card = useRef<HTMLDivElement>(null);
	useTilt(outer, card);

	return (
		<div ref={outer} className={className}>
			<div
				ref={card}
				className={`group/tilt relative overflow-hidden shadow-[calc(var(--sx,0)*1px)_calc(24px+var(--sy,0)*1px)_50px_-28px_rgba(41,77,59,0.55)] transition-[transform,box-shadow] duration-[600ms] ease-expo data-[active]:duration-[140ms] data-[active]:ease-out dark:ring-1 dark:ring-mineral/10 ${radius}`}
			>
				{children}
				<span
					aria-hidden
					className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-data-[active]/tilt:opacity-100"
					style={{
						background:
							'radial-gradient(circle at var(--gx, 50%) var(--gy, 50%), rgba(255,255,255,0.55), rgba(255,255,255,0) 58%)',
						mixBlendMode: 'soft-light',
					}}
				/>
			</div>
		</div>
	);
}
